/**
 * ShopSalesTab Component
 * Handles department sales, volume tracking, coffee tracking, and services
 */
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { 
  PoundSterling, 
  Ticket, 
  Gift, 
  CreditCard, 
  Smartphone,
  Wind,
  Droplets,
  Car,
  Coffee,
  AlertTriangle,
  CheckCircle,
  Lock
} from 'lucide-react';

const formatCurrency = (value) => {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value || 0);
};

const formatAmount = (value) => {
  const num = parseFloat(value);
  return isNaN(num) ? '0.00' : num.toFixed(2);
};

export const ShopSalesTab = ({
  storeDetails,
  tillLock = false,
  tillServiceKeys = [],
  tillVolumeIds = [],
  tillCoffeeFilled = false,
  // Department amounts
  totalSalesAmount,
  setTotalSalesAmount,
  departmentAmounts,
  setDepartmentAmounts,
  departmentVolumes,
  setDepartmentVolumes,
  otherDepartmentsAmount,
  // Service amounts
  serviceAmounts,
  setServiceAmounts,
  serviceKeys,
  enabledServices,
  totalServiceSales,
  totalSales,
  // Coffee tracking
  coffeeTracking,
  updateCoffeeSoldQty,
  updateCoffeeReading,
  // Opening readings state
  openingReadingsLoading,
  openingReadingsError,
  onRetryOpeningReadings,
  // Scratch card
  scratchCardVerified,
  setScratchCardVerified,
  // Handlers
  handleAmountChange,
  handleAmountBlur,
  handleAmountFocus,
  setUserHasInteracted,
  // Toast for notifications
  toast
}) => {
  // Helper to render opening reading value - handles null (loading), error, and zero
  const renderOpeningReading = (sizeData) => {
    if (sizeData?.loading) {
      return <span className="text-muted-foreground animate-pulse">...</span>;
    }
    if (sizeData?.error || sizeData?.opening_reading === null) {
      return <span className="text-red-500 font-bold">!</span>;
    }
    return sizeData?.opening_reading ?? 0;
  };
  
  return (
    <>
      {tillLock && (
        <div className="mb-4 flex items-center gap-2 p-2 rounded-lg border border-blue-500/30 bg-blue-500/10 text-sm text-blue-600 dark:text-blue-400" data-testid="till-lock-banner-shop">
          <Lock className="h-4 w-4" />
          Figures filled from the Day End report are read-only. Fields not on the report (e.g. Empty Kegs) can still be entered.
        </div>
      )}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 [&_input:read-only]:bg-muted [&_input:read-only]:cursor-not-allowed [&_input:read-only]:text-muted-foreground">
        {/* LEFT COLUMN - Department Sales */}
        <div className="space-y-6">
          {/* Sales Amount Section */}
          <Card>
            <CardHeader className="bg-primary/5 border-b border-primary/20 pb-3">
              <CardTitle className="flex items-center gap-2 text-lg" style={{ fontFamily: 'Barlow Condensed' }}>
                <PoundSterling className="h-4 w-4 text-primary" />
                Department Sales
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-3 space-y-2">
              {/* Total Sales (Z-Reading) - at the top */}
              {storeDetails.enable_total_sales && (
                <div className="p-2 bg-primary/10 border border-primary/30 rounded-lg mb-3">
                  <div className="flex items-center gap-3">
                    <Label className="flex-1 font-semibold text-primary text-sm">Total Sales (Z-Reading)</Label>
                    <div className="relative w-28">
                      <span className="absolute left-2 top-1/2 -translate-y-1/2 text-primary text-sm">£</span>
                      <Input
                        type="text"
                        value={totalSalesAmount}
                      readOnly={tillLock}
                        onChange={(e) => {
                          setUserHasInteracted(true);
                          if (e.target.value === '' || /^[0-9]*\.?[0-9]*$/.test(e.target.value)) {
                            setTotalSalesAmount(e.target.value);
                          }
                        }}
                        onBlur={() => {
                          const num = parseFloat(totalSalesAmount);
                          const formatted = isNaN(num) || totalSalesAmount === '' ? '0.00' : num.toFixed(2);
                          setTotalSalesAmount(formatted);
                        }}
                        onFocus={handleAmountFocus}
                        className="pl-6 h-8 text-sm font-mono text-right font-bold border-primary"
                        placeholder="0.00"
                        data-testid="total-sales-amount"
                      />
                    </div>
                  </div>
                </div>
              )}
              
              {storeDetails.departments?.filter(d => d.field_type === 'amount' && d.name.toLowerCase() !== 'fuel sales')
                .sort((a, b) => a.name.localeCompare(b.name))
                .map(dept => (
                <div key={dept.id} className="flex items-center gap-3">
                  <Label className="flex-1 text-sm">{dept.name}</Label>
                  <div className="relative w-28">
                    <span className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">£</span>
                    <Input
                      type="text"
                      value={departmentAmounts[dept.id] || ''}
                      readOnly={tillLock}
                      onChange={(e) => handleAmountChange(dept.id, e.target.value, setDepartmentAmounts, departmentAmounts)}
                      onBlur={() => handleAmountBlur(dept.id, setDepartmentAmounts, departmentAmounts)}
                      onFocus={handleAmountFocus}
                      className="pl-6 h-8 text-sm font-mono text-right"
                      placeholder="0.00"
                      data-testid={`dept-amount-${dept.id}`}
                    />
                  </div>
                </div>
              ))}
              
              {/* Other Departments - at the bottom (calculated) */}
              {storeDetails.enable_other_departments && storeDetails.enable_total_sales && (
                <div className={`p-2 rounded-lg mt-3 ${
                  otherDepartmentsAmount < 0 
                    ? 'bg-red-500/10 border border-red-500/30' 
                    : 'bg-blue-500/10 border border-blue-500/30'
                }`}>
                  <div className="flex items-center gap-3">
                    <Label className="flex-1 font-medium text-sm">
                      Other Departments
                      <span className="text-xs text-muted-foreground block">Auto-calculated</span>
                    </Label>
                    <div className="relative w-28">
                      <span className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">£</span>
                      <Input
                        type="text"
                        value={formatAmount(otherDepartmentsAmount)}
                        readOnly
                        className={`pl-6 h-8 text-sm font-mono text-right bg-muted/50 cursor-not-allowed ${
                          otherDepartmentsAmount < 0 ? 'text-red-500' : ''
                        }`}
                        data-testid="other-departments-amount"
                      />
                    </div>
                  </div>
                  {otherDepartmentsAmount < 0 && (
                    <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                      <AlertTriangle className="h-3 w-3" />
                      Department totals exceed Total Sales.
                    </p>
                  )}
                </div>
              )}
              
            </CardContent>
          </Card>
        </div>

        {/* RIGHT COLUMN - Sales Qty, Coffee Tracking, Services, Summary */}
        <div className="space-y-6">
        
          {/* Sales Qty Section (for volume departments) */}
          {storeDetails.departments?.some(d => d.field_type === 'volume') && (
            <Card className="border-blue-500/30 bg-blue-500/5">
              <CardHeader className="pb-2 pt-3 bg-blue-500/5 border-b border-blue-500/20">
                <CardTitle className="text-base" style={{ fontFamily: 'Barlow Condensed' }}>Sales Qty</CardTitle>
              </CardHeader>
              <CardContent className="pt-3 space-y-2">
                {storeDetails.departments?.filter(d => d.field_type === 'volume').map(dept => (
                  <div key={dept.id} className="flex items-center gap-3">
                    <Label className="flex-1 text-sm">{dept.name}</Label>
                    <Input
                      type="text"
                      inputMode="numeric"
                      value={departmentVolumes[dept.id] || ''}
                      readOnly={tillLock && tillVolumeIds.includes(dept.id)}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val === '' || /^\d+$/.test(val)) {
                          setDepartmentVolumes({
                            ...departmentVolumes,
                            [dept.id]: val
                          });
                        }
                      }}
                      onBlur={() => {
                        const num = parseInt(departmentVolumes[dept.id]) || 0;
                        setDepartmentVolumes({
                          ...departmentVolumes,
                          [dept.id]: num.toString()
                        });
                      }}
                      onFocus={handleAmountFocus}
                      className="w-24 h-8 text-sm font-mono text-right"
                      placeholder="0"
                      data-testid={`dept-volume-${dept.id}`}
                    />
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
          
          {/* Coffee Group - Compact - Only show if store has coffee tracking enabled */}
          {storeDetails?.coffee_tracking && 
           (storeDetails.coffee_tracking.coffee_sold_enabled || storeDetails.coffee_tracking.small_enabled || storeDetails.coffee_tracking.large_enabled) &&
           (coffeeTracking.coffee_sold_enabled || coffeeTracking.small.enabled || coffeeTracking.large.enabled) && (
            <Card className="border-amber-500/30 bg-amber-500/5">
              <CardHeader className="pb-2 pt-3">
                <CardTitle className="flex items-center gap-2 text-base" style={{ fontFamily: 'Barlow Condensed' }}>
                  <Coffee className="h-4 w-4 text-amber-600" />
                  Coffee Tracking
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0 pb-3 space-y-2">
                {/* Coffee Sold Only (no tracking) */}
                {coffeeTracking.coffee_sold_enabled && !coffeeTracking.small.enabled && !coffeeTracking.large.enabled && (
                  <div className="flex items-center gap-3">
                    <Label className="text-sm">Coffee Sold</Label>
                    <Input
                      type="number"
                      value={coffeeTracking.coffee_sold_qty}
                      readOnly={tillLock && tillCoffeeFilled}
                      onChange={(e) => updateCoffeeSoldQty(e.target.value)}
                      placeholder="Qty"
                      className="w-20 h-7 text-xs font-mono"
                      data-testid="coffee-sold-qty"
                    />
                    <span className="text-xs text-muted-foreground">cups</span>
                  </div>
                )}

                {/* POS Sold Qty - Above tracking rows when tracking is enabled */}
                {coffeeTracking.coffee_sold_enabled && (coffeeTracking.small.enabled || coffeeTracking.large.enabled) && (
                  <div className="flex items-center gap-2 text-xs pb-2 border-b border-amber-500/20">
                    <span className="font-medium text-amber-600">POS Sold:</span>
                    <Input
                      type="number"
                      value={coffeeTracking.coffee_sold_qty}
                      readOnly={tillLock && tillCoffeeFilled}
                      onChange={(e) => updateCoffeeSoldQty(e.target.value)}
                      placeholder="Qty"
                      className="w-16 h-6 text-xs font-mono"
                      data-testid="coffee-sold-qty"
                    />
                    <span className="text-muted-foreground">cups</span>
                  </div>
                )}

                {/* Column Headers */}
                {(coffeeTracking.small.enabled || coffeeTracking.large.enabled) && (
                  <div className="grid grid-cols-5 gap-1 text-xs text-muted-foreground font-medium px-1">
                    <span></span>
                    <span className="text-center">Opening</span>
                    <span className="text-center">Closing</span>
                    <span className="text-center">Sold</span>
                    <span></span>
                  </div>
                )}

                {/* Small Tracking Row */}
                {coffeeTracking.small.enabled && (
                  <div className="grid grid-cols-5 gap-1 items-center text-xs">
                    <span className="font-medium text-amber-600">Small:</span>
                    <span className="text-center font-mono">{renderOpeningReading(coffeeTracking.small)}</span>
                    <Input
                      type="number"
                      value={coffeeTracking.small.closing_reading}
                      onChange={(e) => updateCoffeeReading('small', 'closing_reading', e.target.value)}
                      placeholder="0"
                      className="h-6 text-xs font-mono text-center"
                      data-testid="small-coffee-closing"
                      disabled={coffeeTracking.small.loading || coffeeTracking.small.error}
                    />
                    <span className="text-center font-mono font-bold text-blue-600">{coffeeTracking.small.calculated_sold}</span>
                    <span></span>
                  </div>
                )}

                {/* Large Tracking Row */}
                {coffeeTracking.large.enabled && (
                  <div className="grid grid-cols-5 gap-1 items-center text-xs">
                    <span className="font-medium text-amber-600">Large:</span>
                    <span className="text-center font-mono">{renderOpeningReading(coffeeTracking.large)}</span>
                    <Input
                      type="number"
                      value={coffeeTracking.large.closing_reading}
                      onChange={(e) => updateCoffeeReading('large', 'closing_reading', e.target.value)}
                      placeholder="0"
                      className="h-6 text-xs font-mono text-center"
                      data-testid="large-coffee-closing"
                      disabled={coffeeTracking.large.loading || coffeeTracking.large.error}
                    />
                    <span className="text-center font-mono font-bold text-blue-600">{coffeeTracking.large.calculated_sold}</span>
                    <span></span>
                  </div>
                )}

                {/* Error message and retry button for opening readings */}
                {(coffeeTracking.small?.error || coffeeTracking.large?.error || openingReadingsError) && (
                  <div className="flex items-center gap-2 p-2 bg-red-500/10 rounded border border-red-500/20 text-xs">
                    <AlertTriangle className="h-4 w-4 text-red-500 flex-shrink-0" />
                    <span className="text-red-500 flex-1">Failed to load opening readings</span>
                    {onRetryOpeningReadings && (
                      <button 
                        onClick={onRetryOpeningReadings}
                        className="text-primary hover:underline font-medium"
                        type="button"
                      >
                        Retry
                      </button>
                    )}
                  </div>
                )}

                {/* Total Sold Row */}
                {(coffeeTracking.small.enabled || coffeeTracking.large.enabled) && (
                  <div className="grid grid-cols-5 gap-1 items-center text-xs pt-1 border-t border-amber-500/20">
                    <span className="font-semibold">Total Sold:</span>
                    <span></span>
                    <span></span>
                    <span className="text-center font-mono font-bold text-lg text-primary">
                      {(coffeeTracking.small.enabled ? coffeeTracking.small.calculated_sold : 0) + 
                       (coffeeTracking.large.enabled ? coffeeTracking.large.calculated_sold : 0)}
                    </span>
                    <span></span>
                  </div>
                )}

                {/* Variance Warning */}
                {coffeeTracking.coffee_sold_enabled && (coffeeTracking.small.enabled || coffeeTracking.large.enabled) && coffeeTracking.coffee_sold_qty && (
                  (() => {
                    const totalMachine = (coffeeTracking.small.enabled ? coffeeTracking.small.calculated_sold : 0) + 
                                          (coffeeTracking.large.enabled ? coffeeTracking.large.calculated_sold : 0);
                    const posSold = parseInt(coffeeTracking.coffee_sold_qty) || 0;
                    const variance = totalMachine - posSold;
                    
                    if (variance === 0) {
                      return (
                        <div className="flex items-center gap-2 p-2 bg-green-500/20 border border-green-500 rounded text-xs">
                          <CheckCircle className="h-4 w-4 text-green-500" />
                          <span className="font-semibold text-green-500">No Variance - Perfect match!</span>
                        </div>
                      );
                    } else if (variance > 0) {
                      return (
                        <div className="flex items-center gap-2 p-2 bg-red-500/20 border border-red-500 rounded text-xs">
                          <AlertTriangle className="h-4 w-4 text-red-500" />
                          <span className="font-semibold text-red-500">
                            Variance: +{variance} cups - Machine sold more than POS (potential waste/theft)
                          </span>
                        </div>
                      );
                    } else {
                      return (
                        <div className="flex items-center gap-2 p-2 bg-yellow-500/20 border border-yellow-500 rounded text-xs">
                          <AlertTriangle className="h-4 w-4 text-yellow-500" />
                          <span className="font-semibold text-yellow-500">
                            Variance: {variance} cups - POS sold more than machine (data entry error?)
                          </span>
                        </div>
                      );
                    }
                  })()
                )}
              </CardContent>
            </Card>
          )}

          {/* Additional Services Sales (Lottery, Scratch Cards, etc.) */}
          {(serviceKeys.length > 0 || enabledServices.length > 0) && (
            <Card>
              <CardHeader className="bg-yellow-500/5 border-b border-yellow-500/20 pb-3">
                <CardTitle className="flex items-center gap-2 text-lg" style={{ fontFamily: 'Barlow Condensed' }}>
                  <Ticket className="h-4 w-4 text-yellow-500" />
                  Services Sales
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-3 space-y-2">
                {/* Lottery Sales */}
                {(serviceKeys.includes('lottery') || enabledServices.some(s => s.key === 'lottery')) && (
                  <div className="flex items-center gap-3">
                    <Label className="flex-1 flex items-center gap-2 text-sm">
                      <Ticket className="h-3.5 w-3.5 text-yellow-500" />
                      Lottery Sales
                    </Label>
                    <div className="relative w-28">
                      <span className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">£</span>
                      <Input
                        type="text"
                        value={serviceAmounts['lottery'] || ''}
                        readOnly={tillLock && tillServiceKeys.includes('lottery')}
                        onChange={(e) => handleAmountChange('lottery', e.target.value, setServiceAmounts, serviceAmounts)}
                        onBlur={() => handleAmountBlur('lottery', setServiceAmounts, serviceAmounts)}
                        onFocus={handleAmountFocus}
                        className="pl-6 h-8 text-sm font-mono text-right"
                        placeholder="0.00"
                        data-testid="service-lottery"
                      />
                    </div>
                  </div>
                )}

                {/* Scratch Cards Sales */}
                {(serviceKeys.includes('scratch_cards') || enabledServices.some(s => s.key === 'scratch_cards')) && (
                  <div className="flex items-center gap-3">
                    <Label className="flex-1 flex items-center gap-2 text-sm">
                      <Gift className="h-3.5 w-3.5 text-purple-500" />
                      Scratch Cards
                      {scratchCardVerified && (
                        <CheckCircle className="h-3.5 w-3.5 text-green-500" />
                      )}
                    </Label>
                    <div className="relative w-28">
                      <span className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">£</span>
                      <Input
                        type="text"
                        value={serviceAmounts['scratch_cards'] || ''}
                        readOnly={tillLock && tillServiceKeys.includes('scratch_cards')}
                        onChange={(e) => {
                          handleAmountChange('scratch_cards', e.target.value, setServiceAmounts, serviceAmounts);
                          if (scratchCardVerified) {
                            setScratchCardVerified(false);
                            toast.info('Scratch card amount changed - please re-verify stock count');
                          }
                        }}
                        onBlur={() => handleAmountBlur('scratch_cards', setServiceAmounts, serviceAmounts)}
                        onFocus={handleAmountFocus}
                        className="pl-6 h-8 text-sm font-mono text-right"
                        placeholder="0.00"
                        data-testid="service-scratch-cards"
                      />
                    </div>
                  </div>
                )}

                {/* PayPoint */}
                {(serviceKeys.includes('paypoint') || enabledServices.some(s => s.key === 'paypoint')) && (
                  <div className="flex items-center gap-3">
                    <Label className="flex-1 flex items-center gap-2 text-sm">
                      <CreditCard className="h-3.5 w-3.5 text-green-500" />
                      PayPoint
                    </Label>
                    <div className="relative w-28">
                      <span className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">£</span>
                      <Input
                        type="text"
                        value={serviceAmounts['paypoint'] || ''}
                        readOnly={tillLock && tillServiceKeys.includes('paypoint')}
                        onChange={(e) => handleAmountChange('paypoint', e.target.value, setServiceAmounts, serviceAmounts)}
                        onBlur={() => handleAmountBlur('paypoint', setServiceAmounts, serviceAmounts)}
                        onFocus={handleAmountFocus}
                        className="pl-6 h-8 text-sm font-mono text-right"
                        placeholder="0.00"
                        data-testid="service-paypoint"
                      />
                    </div>
                  </div>
                )}

                {/* Payzone */}
                {(serviceKeys.includes('payzone') || enabledServices.some(s => s.key === 'payzone')) && (
                  <div className="flex items-center gap-3">
                    <Label className="flex-1 flex items-center gap-2 text-sm">
                      <Smartphone className="h-3.5 w-3.5 text-blue-500" />
                      Payzone
                    </Label>
                    <div className="relative w-28">
                      <span className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">£</span>
                      <Input
                        type="text"
                        value={serviceAmounts['payzone'] || ''}
                        readOnly={tillLock && tillServiceKeys.includes('payzone')}
                        onChange={(e) => handleAmountChange('payzone', e.target.value, setServiceAmounts, serviceAmounts)}
                        onBlur={() => handleAmountBlur('payzone', setServiceAmounts, serviceAmounts)}
                        onFocus={handleAmountFocus}
                        className="pl-6 h-8 text-sm font-mono text-right"
                        placeholder="0.00"
                        data-testid="service-payzone"
                      />
                    </div>
                  </div>
                )}

                {/* Western Union */}
                {(serviceKeys.includes('western_union') || enabledServices.some(s => s.key === 'western_union')) && (
                  <div className="flex items-center gap-3">
                    <Label className="flex-1 text-sm">Western Union</Label>
                    <div className="relative w-28">
                      <span className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">£</span>
                      <Input
                        type="text"
                        value={serviceAmounts['western_union'] || ''}
                        readOnly={tillLock && tillServiceKeys.includes('western_union')}
                        onChange={(e) => handleAmountChange('western_union', e.target.value, setServiceAmounts, serviceAmounts)}
                        onBlur={() => handleAmountBlur('western_union', setServiceAmounts, serviceAmounts)}
                        onFocus={handleAmountFocus}
                        className="pl-6 h-8 text-sm font-mono text-right"
                        placeholder="0.00"
                        data-testid="service-western-union"
                      />
                    </div>
                  </div>
                )}

                {/* MoneyGram */}
                {(serviceKeys.includes('moneygram') || enabledServices.some(s => s.key === 'moneygram')) && (
                  <div className="flex items-center gap-3">
                    <Label className="flex-1 text-sm">MoneyGram</Label>
                    <div className="relative w-28">
                      <span className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">£</span>
                      <Input
                        type="text"
                        value={serviceAmounts['moneygram'] || ''}
                        readOnly={tillLock && tillServiceKeys.includes('moneygram')}
                        onChange={(e) => handleAmountChange('moneygram', e.target.value, setServiceAmounts, serviceAmounts)}
                        onBlur={() => handleAmountBlur('moneygram', setServiceAmounts, serviceAmounts)}
                        onFocus={handleAmountFocus}
                        className="pl-6 h-8 text-sm font-mono text-right"
                        placeholder="0.00"
                        data-testid="service-moneygram"
                      />
                    </div>
                  </div>
                )}

                {/* Fuel Station Services */}
                {(serviceKeys.includes('air_machine') || enabledServices.some(s => s.key === 'air_machine')) && (
                  <div className="flex items-center gap-3">
                    <Label className="flex-1 flex items-center gap-2 text-sm">
                      <Wind className="h-4 w-4 text-blue-500" />
                      Air Machine
                    </Label>
                    <div className="relative w-32">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">£</span>
                      <Input
                        type="text"
                        value={serviceAmounts['air_machine'] || ''}
                        readOnly={tillLock && tillServiceKeys.includes('air_machine')}
                        onChange={(e) => handleAmountChange('air_machine', e.target.value, setServiceAmounts, serviceAmounts)}
                        onBlur={() => handleAmountBlur('air_machine', setServiceAmounts, serviceAmounts)}
                        onFocus={handleAmountFocus}
                        className="pl-7 font-mono text-right"
                        placeholder="0.00"
                        data-testid="service-air-machine"
                      />
                    </div>
                  </div>
                )}

                {(serviceKeys.includes('jet_wash') || enabledServices.some(s => s.key === 'jet_wash')) && (
                  <div className="flex items-center gap-4">
                    <Label className="flex-1 flex items-center gap-2">
                      <Droplets className="h-4 w-4 text-cyan-500" />
                      Jet Wash
                    </Label>
                    <div className="relative w-32">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">£</span>
                      <Input
                        type="text"
                        value={serviceAmounts['jet_wash'] || ''}
                        readOnly={tillLock && tillServiceKeys.includes('jet_wash')}
                        onChange={(e) => handleAmountChange('jet_wash', e.target.value, setServiceAmounts, serviceAmounts)}
                        onBlur={() => handleAmountBlur('jet_wash', setServiceAmounts, serviceAmounts)}
                        onFocus={handleAmountFocus}
                        className="pl-7 font-mono text-right"
                        placeholder="0.00"
                        data-testid="service-jet-wash"
                      />
                    </div>
                  </div>
                )}

                {(serviceKeys.includes('car_wash') || enabledServices.some(s => s.key === 'car_wash')) && (
                  <div className="flex items-center gap-4">
                    <Label className="flex-1 flex items-center gap-2">
                      <Car className="h-4 w-4 text-orange-500" />
                      Car Wash
                    </Label>
                    <div className="relative w-32">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">£</span>
                      <Input
                        type="text"
                        value={serviceAmounts['car_wash'] || ''}
                        readOnly={tillLock && tillServiceKeys.includes('car_wash')}
                        onChange={(e) => handleAmountChange('car_wash', e.target.value, setServiceAmounts, serviceAmounts)}
                        onBlur={() => handleAmountBlur('car_wash', setServiceAmounts, serviceAmounts)}
                        onFocus={handleAmountFocus}
                        className="pl-7 font-mono text-right"
                        placeholder="0.00"
                        data-testid="service-car-wash"
                      />
                    </div>
                  </div>
                )}

                <div className="border-t border-border pt-4 mt-4">
                  <div className="flex items-center justify-between text-sm text-muted-foreground">
                    <span>Services Subtotal</span>
                    <span className="font-mono">{formatCurrency(totalServiceSales)}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Total Sales Summary */}
          <Card className="bg-primary/5 border-primary/20">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <span className="font-semibold text-lg">Total Sales (Dept + Services)</span>
                <span className="text-2xl font-bold font-mono text-primary">
                  {formatCurrency(totalSales)}
                </span>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  );
};

export default ShopSalesTab;
